import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A panel that comes up from the bottom of the screen.
 *
 * A bottom sheet rather than a centred dialog, because this is a phone held in
 * one hand: what the sheet asks for is typed with a thumb, and a box floating
 * in the middle of the viewport puts its field under the reach of that thumb
 * and its buttons above it.
 *
 * Built on Radix Dialog, so the focus trap, the escape key, the scroll lock
 * and `aria-modal` are all handled — none of which is worth reimplementing and
 * all of which is routinely forgotten.
 */

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export function SheetContent({
  className,
  children,
  title,
  description,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  title: string;
  description?: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-50 bg-inverse/40 backdrop-blur-[2px]',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        )}
      />
      <DialogPrimitive.Content
        /*
         * Radix warns when a dialog has no description, and the obvious
         * placation — a hidden description repeating the title — is worse than
         * the warning: a screen reader then announces the title twice, once as
         * the name and once as the description.
         *
         * Opting out explicitly is what Radix provides for a sheet whose title
         * says everything. Spread conditionally, because passing the prop at
         * all would override the id Radix wires up when there IS a
         * description.
         */
        {...(description ? {} : { 'aria-describedby': undefined })}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-app',
          'rounded-t-2xl border border-line bg-raised shadow-xl',
          /*
           * The keyboard is what makes this layout hard: it covers the bottom
           * of the viewport, which is exactly where this sheet lives and where
           * its one field sits. `max-h` against the small viewport unit keeps
           * the sheet inside whatever space the keyboard leaves, and the body
           * scrolls within it rather than the field disappearing under the
           * keys.
           */
          'max-h-[90svh] overflow-y-auto',
          'pb-[max(env(safe-area-inset-bottom),var(--space-5))]',
          'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
          'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
          'duration-[var(--motion-duration-base)]',
          className,
        )}
        {...props}
      >
        {/* The grab handle. Decorative — the sheet is dismissed with the
            close button, Escape, or the overlay, all of which are real
            controls a keyboard can reach. */}
        <div className="flex justify-center pb-1 pt-3" aria-hidden="true">
          <span className="h-1 w-10 rounded-full bg-muted" />
        </div>

        <div className="flex items-start gap-3 px-5 pb-1 pt-2">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-xl font-bold tracking-tight text-ink">
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-[0.9375rem] leading-normal text-ink-muted">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>

          <DialogPrimitive.Close
            className={cn(
              'grid size-control-sm shrink-0 place-items-center rounded-full text-ink-subtle',
              'transition-colors duration-[var(--motion-duration-fast)] ease-standard',
              'hover:bg-muted hover:text-ink',
            )}
          >
            <X className="size-5" aria-hidden="true" />
            <span className="sr-only-text">Close</span>
          </DialogPrimitive.Close>
        </div>

        <div className="px-5 pt-3">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
